import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export interface FunnelStageNode<K extends string = string> {
  key: K;
  label: string;
  value: number;      // número absoluto exibido dentro da banda
  caption?: string;   // texto secundário
  color: string;      // ex.: "var(--chart-4)" — usado no gradient + ring ativo
  /**
   * Opcional — substitui o valor formatado padrão.
   * Útil para estágios cujo "valor" é monetário (R$ 17.681) em vez de contagem.
   */
  displayValue?: string;
}

interface FunnelVizProps<K extends string> {
  title: string;
  stages: FunnelStageNode<K>[];
  rates: string[];              // N-1 entradas: rates[i] = stage[i] → stage[i+1]
  activeStage: K | null;
  onStageClick: (key: K) => void;
  description?: string;
  hint?: string;
}

export function FunnelViz<K extends string>({
  title,
  stages,
  rates,
  activeStage,
  onStageClick,
  description,
  hint = "Clique em qualquer estágio para ver a listagem dos itens",
}: FunnelVizProps<K>) {
  const max = Math.max(...stages.map((s) => s.value), 1);

  return (
    <section className="flex h-full flex-col rounded-lg border border-border-subtle bg-bg-card p-6 space-y-5">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold tracking-tight text-text-primary">{title}</h2>
        {description && <p className="text-[13px] text-text-muted">{description}</p>}
      </div>

      {/* Wrapper flex centra verticalmente sem afetar o layout interno das bandas */}
      <div className="flex flex-1 items-center">
        <div className="relative mx-auto w-full max-w-2xl">
          {stages.map((stage, i) => {
            const widthPct = 35 + (stage.value / max) * 55;
            const isActive = activeStage === stage.key;
            const isLast = i === stages.length - 1;
            const stageColor = stage.color;

            return (
              <div key={stage.key}>
                <motion.button
                  type="button"
                  onClick={() => onStageClick(stage.key)}
                  initial={{ opacity: 0, y: 14, scaleY: 0.7 }}
                  animate={{ opacity: 1, y: 0, scaleY: 1 }}
                  transition={{ delay: i * 0.12, duration: 0.45, ease: [0.215, 0.61, 0.355, 1] }}
                  whileHover={{ scale: 1.015, y: -1 }}
                  whileTap={{ scale: 0.985 }}
                  className={cn(
                    "group relative mx-auto block w-full overflow-hidden py-5 transition",
                    "focus-visible:outline-none",
                    isActive && "ring-2 ring-offset-2 ring-offset-bg-card"
                  )}
                  style={{
                    width: `${widthPct}%`,
                    clipPath: "polygon(6% 0, 94% 0, 88% 100%, 12% 100%)",
                    background: `linear-gradient(135deg,
                      hsl(${stageColor} / 0.78) 0%,
                      hsl(${stageColor}) 100%)`,
                    boxShadow: `inset 0 -20px 40px -20px hsl(${stageColor} / 0.6)`,
                    ...(isActive && {
                      ["--tw-ring-color" as never]: `hsl(${stageColor})`,
                    }),
                  }}
                >
                  <div className="flex flex-col items-center justify-center gap-0.5 text-white drop-shadow-sm">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] opacity-90">
                      {stage.label}
                    </span>
                    <span className="text-3xl font-bold tracking-tight">
                      {stage.displayValue ?? stage.value.toLocaleString("pt-BR")}
                    </span>
                    {stage.caption && (
                      <span className="text-[11px] opacity-80">{stage.caption}</span>
                    )}
                  </div>

                  {/* Brilho diagonal no hover */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.22) 50%, transparent 60%)",
                    }}
                  />
                </motion.button>

                {/* Taxa entre estágios — herda a cor da etapa seguinte */}
                {!isLast && rates[i] && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.12 + 0.3, duration: 0.3 }}
                    className="flex items-center justify-center gap-2 py-1"
                  >
                    <span className="h-px w-8 bg-border-strong" />
                    <span
                      className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      style={{
                        color: `hsl(${stages[i + 1].color})`,
                        backgroundColor: `hsl(${stages[i + 1].color} / 0.15)`,
                      }}
                    >
                      {rates[i]}
                    </span>
                    <span className="h-px w-8 bg-border-strong" />
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-center text-xs text-text-muted">{hint}</p>
    </section>
  );
}
